<script>
import Title from "@/components/admin/common/TitleArea.vue"
import StatusBadge from "@/components/fulfillment/StatusBadge.vue"
import DataTable from "@/components/fulfillment/DataTable.vue"
import PartnerSkuRequestNew from "@/components/admin/fulfillment/modal/PartnerSkuRequestNew.vue"
import PageGuideDialog from "@/components/admin/fulfillment/guide/PageGuideDialog.vue"
import PartnerFulfillmentGuide from "@/components/admin/fulfillment/guide/PartnerFulfillmentGuide.vue"

export default {
  name: 'PartnerSkuRequestList',
  components: {
    Title,
    StatusBadge,
    DataTable,
    PartnerSkuRequestNew,
    PageGuideDialog,
    PartnerFulfillmentGuide
  },
  data() {
    return {
      description: {
        title: '내 상품목록 관리'
      },
      statusOptions: [
        { label: '전체', value: '' },
        { label: '검토 중', value: 'REQUESTED' },
        { label: '정상 연결', value: 'APPROVED' },
        { label: '반려', value: 'REJECTED' },
      ],
      filters: {
        status: '',
        brand_id: '',
        category_id: '',
        q: ''
      },
      rows: [],
      summary: {
        total: 0,
        requested: 0,
        rejected: 0,
        approved: 0,
      },
      brands: [],
      categories: [],
      loading: false,
      loadingMore: false,
      hasNext: false,
      masterDataLoading: false,
      pagination: { page: 1, rowsPerPage: 20 },
      showNewModal: false,
      guideDialog: false,
    }
  },
  computed: {
    brandOptions() {
      return [{ brand_id: '', name: '전체' }, ...this.brands]
    },
    categoryOptions() {
      return [{ category_id: '', name: '전체' }, ...this.categories]
    },
    tablePagination() {
      return { page: 1, rowsPerPage: 0 }
    },
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.applyRouteContext()
      }
    }
  },
  methods: {
    applyRouteContext() {
      const routeTitle = String(this.$route?.meta?.title || '').replace(/^Aone 관리자 \|\s*/, '')
      if (routeTitle) {
        this.description.title = routeTitle
      }
      this.showNewModal = this.$route.path === '/admin/fulfillment/request/new'
    },
    normalizeRows(data) {
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.list)
            ? data.list
            : []

      return items.map((row, index) => ({
        ...row,
        row_key: row.request_id || `${row.partner_id || 'partner'}-${row.partner_sku_code || 'sku'}-${row.status || 'status'}-${index}`,
      }))
    },
    buildListQuery(extra = {}) {
      return new URLSearchParams({
        status: extra.status ?? this.filters.status ?? '',
        brand_id: extra.brand_id ?? this.filters.brand_id ?? '',
        category_id: extra.category_id ?? this.filters.category_id ?? '',
        q: extra.q ?? this.filters.q ?? '',
        page: String(extra.page ?? this.pagination.page ?? 1),
        pageSize: String(extra.pageSize ?? this.pagination.rowsPerPage ?? 20),
        lazy: extra.lazy ?? '1',
      }).toString()
    },
    async loadFilterOptions() {
      this.masterDataLoading = true
      const config = {
        method: 'GET',
        url: '/api/fulfillment/partner/sku-requests/filter-options',
        name: '[ Fulfillment / Partner SKU 필터 옵션 ]'
      }
      await this.$http(config)
        .then(res => {
          this.brands = res.data?.data?.brands || []
          this.categories = res.data?.data?.categories || []
        })
        .catch(() => {
          this.brands = []
          this.categories = []
        })
      this.masterDataLoading = false
    },
    async loadSummary() {
      try {
        const res = await this.$http({
          method: 'GET',
          url: '/api/fulfillment/partner/sku-products/dashboard',
          name: '[ Fulfillment / Partner SKU 요약 ]'
        })
        const data = res.data?.data || {}
        const products = data.products || {}
        const requests = data.requests || {}
        const approved = Number(products.total || 0)
        const requested = Number(requests.requested || 0)
        const rejected = Number(requests.rejected || 0)
        this.summary = {
          total: approved + requested + rejected,
          requested,
          rejected,
          approved,
        }
      } catch (_) {
        this.summary = {
          total: 0,
          requested: 0,
          rejected: 0,
          approved: 0,
        }
      }
    },
    async loadList({ append = false } = {}) {
      if (append) {
        if (this.loadingMore || !this.hasNext) return
        this.loadingMore = true
      } else {
        this.loading = true
      }
      const config = {
        method: 'GET',
        url: `/api/fulfillment/partner/sku-requests?${this.buildListQuery()}`,
        name: '[ Fulfillment / Partner SKU 요청 목록 ]'
      }
      await this.$http(config)
        .then(res => {
          const data = res.data?.data
          const nextRows = this.normalizeRows(data)
          this.rows = append ? [...this.rows, ...nextRows] : nextRows
          this.hasNext = Boolean(data?.hasNext)
        })
        .catch(() => {
          if (!append) this.rows = []
          this.hasNext = false
        })
      if (append) {
        this.loadingMore = false
      } else {
        this.loading = false
      }
    },
    async loadInitialData() {
      this.pagination.page = 1
      this.hasNext = false
      await Promise.all([
        this.loadSummary(),
        this.loadList(),
      ])
    },
    onLoadMore() {
      if (this.loading || this.loadingMore || !this.hasNext) return
      this.pagination.page += 1
      this.loadList({ append: true })
    },
    formatDateTime(value) {
      return value ? this.$formatDate(value, 'YYYY-MM-DD HH:mm:ss') : '-'
    },
    getRequestTypeLabel(row) {
      if (row.request_type === 'LINK_REQUEST') return '연결요청'
      if (row.request_type === 'NEW_REQUEST') return '신규등록요청'
      return '정상 연결'
    },
    getStatusBadgeStatus(row) {
      return row.status === 'APPROVED' ? 'MAPPED' : row.status
    },
    goToDetail(row) {
      if (!row?.partner_sku_code) return
      this.$router.push({
        path: '/admin/fulfillment/my-products/detail',
        query: {
          partner_sku_code: row.partner_sku_code,
          returnQuery: JSON.stringify(this.$route.query || {})
        }
      })
    },
    onRowClick({ row }) {
      this.goToDetail(row)
    },
    goNew() {
      this.openNewModal()
    },
    openNewModal() {
      this.showNewModal = true
    },
    closeNewModal() {
      this.showNewModal = false
      if (this.$route.path === '/admin/fulfillment/request/new') {
        this.$router.replace('/admin/fulfillment/sku-requests')
      }
    },
    onCreated() {
      this.loadList()
      this.closeNewModal()
    },
    onSearch() {
      this.pagination.page = 1
      this.hasNext = false
      this.loadList()
    }
  },
  mounted() {
    this.applyRouteContext()
    this.loadFilterOptions()
    this.loadInitialData()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description">
          <template #title-action>
            <q-btn flat round dense size="sm" color="primary" icon="help_outline" aria-label="내 상품목록 관리 사용 가이드" @click="guideDialog = true">
              <q-tooltip>내 상품목록 관리 사용 가이드</q-tooltip>
            </q-btn>
          </template>
          <q-btn
            color="primary"
            label="상품 요청 등록"
            @click="goNew"
          />
        </Title>
        <div class="text-caption text-grey-7 q-mt-sm">
          내가 등록했거나 요청한 상품을 브랜드, 카테고리, 상태 기준으로 검색하고 관리합니다.
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-md q-mb-md items-stretch">
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="text-caption text-grey">전체 상품</div>
          <div class="text-h5 q-mt-sm">{{ summary.total }}</div>
          <div class="text-caption text-grey q-mt-sm">연결 완료와 요청 중인 상품을 함께 봅니다.</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="text-caption text-grey">검토 중</div>
          <div class="text-h5 q-mt-sm">{{ summary.requested }}</div>
          <div class="text-caption text-grey q-mt-sm">운영팀 검토를 기다리는 건입니다.</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="text-caption text-grey">반려</div>
          <div class="text-h5 q-mt-sm">{{ summary.rejected }}</div>
          <div class="text-caption text-grey q-mt-sm">수정 후 다시 요청이 필요한 건입니다.</div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="comp-box q-pa-md dashboard-card">
          <div class="text-caption text-grey">정상 연결</div>
          <div class="text-h5 q-mt-sm">{{ summary.approved }}</div>
          <div class="text-caption text-grey q-mt-sm">기준 상품과 연결되었거나 처리가 완료된 건입니다.</div>
        </div>
      </div>
    </div>

    <div class="row q-mb-lg">
      <div class="col-12 col-md-12 col-lg-10 q-mb-md">
        <div class="comp-box q-pa-md">
          <div class="row q-col-gutter-lg">
            <div class="col-12 col-md-3">
              <q-select
                v-model="filters.status"
                :options="statusOptions"
                label="상태"
                emit-value
                map-options
                dense
                outlined
              />
            </div>
            <div class="col-12 col-md-3">
              <q-select
                v-model="filters.brand_id"
                :options="brandOptions"
                option-label="name"
                option-value="brand_id"
                label="브랜드"
                emit-value
                map-options
                dense
                outlined
                :loading="masterDataLoading"
              />
            </div>
            <div class="col-12 col-md-3">
              <q-select
                v-model="filters.category_id"
                :options="categoryOptions"
                option-label="name"
                option-value="category_id"
                label="카테고리"
                emit-value
                map-options
                dense
                outlined
                :loading="masterDataLoading"
              />
            </div>
            <div class="col-12 col-md-3">
              <q-input
                v-model="filters.q"
                label="검색 (내 SKU / 바코드 / 브랜드 / 카테고리)"
                dense
                outlined
                @keyup.enter="onSearch"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-12 col-lg-2 q-mb-md">
        <q-btn
          color="primary"
          size="lg"
          label="검색"
          class="full-width"
          style="height:100%"
          @click="onSearch"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="comp-box">
          <DataTable
            :rows="rows"
            :columns="[
              { name: 'request_id', label: '요청 ID', field: 'request_id', align: 'left' },
              { name: 'partner_sku_code', label: '내 SKU', field: 'partner_sku_code', align: 'left' },
              { name: 'barcode', label: '바코드', field: row => row.barcode || '-', align: 'left' },
              { name: 'brand_name', label: '브랜드', field: row => row.brand_name || '-', align: 'left' },
              { name: 'category_name', label: '카테고리', field: row => row.category_name || '-', align: 'left' },
              { name: 'hs_code', label: 'HS CODE', field: 'hs_code', align: 'left' },
              { name: 'spec_type', label: '규격 타입', field: 'spec_type', align: 'left' },
              { name: 'request_type', label: '요청 유형', field: row => getRequestTypeLabel(row), align: 'center' },
              { name: 'status', label: '처리 상태', field: row => getStatusBadgeStatus(row), align: 'center' },
              { name: 'created_at', label: '최근 요청일', field: row => formatDateTime(row.created_at), align: 'left' },
              { name: 'actions', label: '', field: 'actions', align: 'center' },
            ]"
            row-key="row_key"
            :loading="loading"
            :pagination="tablePagination"
            @row-click="onRowClick"
          >
            <template #body-cell-status="props">
              <q-td :props="props">
                <StatusBadge :status="props.value" />
              </q-td>
            </template>
            <template #body-cell-actions="props">
              <q-td :props="props">
                <q-btn flat round dense color="primary" icon="open_in_new" aria-label="내 상품 상세보기" @click.stop="goToDetail(props.row)">
                  <q-tooltip>내 상품 상세보기</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </DataTable>
          <div class="row items-center justify-between q-pa-md table-load-more">
            <div class="text-caption text-grey-7">
              현재 {{ rows.length }}건 표시
            </div>
            <q-btn
              v-if="hasNext"
              outline
              color="primary"
              label="더보기"
              :loading="loadingMore"
              :disable="loading || loadingMore"
              @click="onLoadMore"
            />
          </div>
        </div>
      </div>
    </div>

    <PageGuideDialog
      v-model="guideDialog"
      title="내 상품목록 관리 사용 가이드"
      description="내 상품 검색과 요청 상태 확인 방법입니다."
    >
      <PartnerFulfillmentGuide guide-key="productList" />
    </PageGuideDialog>
  </q-page>

  <teleport to="body">
    <q-dialog v-model="showNewModal" 
      maximized 
      persistent 
      @hide="closeNewModal">
      <PartnerSkuRequestNew
        :in-modal="true"
        @created="onCreated"
        @close="closeNewModal"
      />
    </q-dialog>
  </teleport>
</template>

<style scoped lang="scss">
.dashboard-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.table-load-more {
  border-top: 1px solid #edf0f2;
}
</style>
